import { Fragment } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  listById,
  listLabel,
  required,
  styled,
  ToucanFab,
  ToucanSearchField,
  useSearchDocked,
} from "toucan";
import { AlbumCard } from "../components/AlbumCard.tsx";
import { HeadingJump } from "../components/HeadingJump.tsx";
import { ShelfChips } from "../components/ShelfChips.tsx";
import { SortMenu } from "../components/SortMenu.tsx";
import { fillReleaseDates, shownAlbums } from "../library.ts";
import {
  albumHeadings,
  albumSections,
  jumpMarks,
  searchAlbums,
  sortAlbums,
  unshelvedAlbums,
} from "../model.ts";
import type { Album, AlbumSort } from "../model.ts";
import { albumPlaying } from "../playback.ts";
import type { Playing } from "../playback.ts";
import { go } from "../routes.ts";
import type { Route } from "../routes.ts";
import { shelves, store } from "../state.ts";

const Page = styled("section")`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  background: var(--toucan-canvas);

  .toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  /* The search box gives up whatever width the sort control needs, rather than
     the other way round: a search box narrowed by a word or two is still a
     search box, while a sort pill that shrinks stops naming the order. */
  .controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .controls .search {
    flex: 1 1 auto;
    min-width: 0;
  }

  /* The tiles have no border, so the sleeves alone separate them. */
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(8.5rem, 1fr));
    gap: 1rem 0.75rem;
    /* Room below the last row for the floating button to sit over, and for a
       jump to bring the last heading to the top (HeadingJump). */
    padding-bottom: calc(4rem + var(--jump-room, 0px));
  }

  /* A heading spans the grid and starts a new row of tiles. */
  .heading {
    grid-column: 1 / -1;
    margin: 0.5rem 0 0;
    font-size: 1rem;
    color: var(--toucan-heading);
  }

  .heading:first-child {
    margin-top: 0;
  }

  /* A search that found nothing spans the grid rather than sitting in its
     first cell. */
  .empty {
    grid-column: 1 / -1;
    margin: 0;
    color: var(--toucan-muted);
  }

  /* The add button is fixed to the viewport, so it doesn't scroll away. */
  & > .fab {
    position: fixed;
    right: 16px;
    bottom: 16px;
  }

  /* The jump strip runs down the right-hand edge of the scrolling pane, from
     just under its top to just over the add button. */
  & > .jump {
    position: fixed;
    top: calc(var(--pane-top) + 0.5rem);
    right: 0.25rem;
    bottom: calc(16px + 56px + 0.5rem + env(safe-area-inset-bottom, 0px));
    z-index: 1;
  }

  /* On iOS the pane starts under the bar, and the add button is in the bar,
     so the strip runs from below the one to the foot of the screen. */
  :root[data-platform="ios"] & > .jump {
    top: calc(var(--pane-top) + var(--toucan-bar-bottom, 0px) + 0.5rem);
    bottom: calc(0.5rem + env(safe-area-inset-bottom, 0px));
  }

  /* A band drawn to the strip's edge would sit on a desktop scrollbar, where
     the letters' empty margin doesn't matter. */
  &[data-jump="sized"] > .jump {
    right: calc(var(--pane-gutter) + 0.25rem);
  }

  /* Clear of the strip, whose width is its own, the toolbar too, since the
     strip reaches to the top of the pane. */
  &[data-jump="even"] {
    padding-right: 3.25rem;
  }

  &[data-jump="sized"] {
    padding-right: calc(var(--pane-gutter) + 3.25rem);
  }
`;

/** Which shelf the route is filtering to: its id, "" for Unshelved, or none. */
function pressedChip(route: Route): string | null {
  if (route.name === "shelf") {
    return route.id;
  }
  return route.name === "unshelved" ? "" : null;
}

/** Where a filter chip leads, to one shelf or the unshelved albums or all. */
function chipPath(shelfId: string | null): string {
  if (shelfId === null) {
    return "";
  }
  return shelfId === "" ? "unshelved" : `shelf/${shelfId}`;
}

/**
 * A tile's shelf badge, left off while the grid is filtered to that shelf,
 * since the pressed chip already says it.
 */
function badge(album: Album, route: Route): string | undefined {
  if (
    album.shelfId === undefined ||
    (route.name === "shelf" && route.id === album.shelfId)
  ) {
    return undefined;
  }
  const shelf = listById(store().shelves, album.shelfId);
  return shelf === undefined ? undefined : listLabel(shelf);
}

/** Whether a tile is the record playing, and if so whether it is paused. */
function tileState(
  playing: Playing | null,
  albumId: string,
): { paused: boolean } | null {
  return albumPlaying(playing, albumId)
    ? { paused: required(playing, "playing").paused }
    : null;
}

/**
 * Whether the grid has a jump strip, and whether its headings get room by
 * album count, as release dates do because they cluster.
 */
function stripKind(marks: readonly unknown[], sort: AlbumSort): string {
  if (marks.length === 0) {
    return "none";
  }
  return sort.field === "release" ? "sized" : "even";
}

/**
 * The library, and the home screen: every album you have saved, as tiles. It
 * is made of Toucan's own synced data, so it works offline.
 */
export function LibraryPage({
  route,
  sort,
  onSort,
  filter,
  onFilter,
  playing,
}: {
  route: Route;
  sort: AlbumSort;
  onSort: (sort: AlbumSort) => void;
  filter: string;
  onFilter: (query: string) => void;
  playing: Playing | null;
}) {
  const albums = shownAlbums(route);
  const shown = searchAlbums(sortAlbums(albums, sort), filter);
  const headings = albumHeadings(sort, albums, Date.now());
  const sections = albumSections(shown, headings.of);
  const marks = jumpMarks(sections, headings.mark);
  const strip = stripKind(marks, sort);
  // Search and sort share the top line while the chips fit, and the sort
  // moves down beside their pill once they fold. With the search docked, the
  // sort has the chips' line throughout.
  const docked = useSearchDocked();
  const [folded, setFolded] = useState(false);
  const grid = useRef<HTMLDivElement>(null);
  const [paneTop, setPaneTop] = useState(0);
  const [paneGutter, setPaneGutter] = useState(0);

  // Where the pane starts, for the strip's top, and how wide its scrollbar is,
  // which is none where it floats over the page.
  useEffect(() => {
    const view = required(
      required(grid.current, "grid").closest("main"),
      "main",
    );
    const measure = () => {
      setPaneTop(view.getBoundingClientRect().top);
      setPaneGutter(view.offsetWidth - view.clientWidth);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
    };
  }, [sort, filter, route, shown.length]);
  const sortMenu = (
    <SortMenu
      sort={sort}
      onSort={(next) => {
        onSort(next);
        // Some albums have no release date stored; fetch them only when
        // someone sorts by it.
        if (next.field === "release") {
          void fillReleaseDates();
        }
      }}
    />
  );

  return (
    <Page
      data-jump={strip}
      style={`--pane-top: ${String(paneTop)}px; --pane-gutter: ${String(paneGutter)}px`}
    >
      <div class="toolbar">
        <div class="controls">
          <ToucanSearchField
            class="search"
            docked
            label="Search albums"
            placeholder="Search albums…"
            query={filter}
            onQuery={onFilter}
          />
          {!docked && !folded && sortMenu}
        </div>
        <ShelfChips
          shelves={shelves()}
          unshelved={unshelvedAlbums(store()).length > 0}
          pressed={pressedChip(route)}
          onPick={(shelfId) => {
            go(chipPath(shelfId));
          }}
          aside={sortMenu}
          asideOnceFolded={!docked}
          onFold={setFolded}
        />
      </div>
      <div class="grid" ref={grid}>
        {shown.length === 0 && filter.trim() !== "" && (
          <p class="empty">No album here matches “{filter.trim()}”.</p>
        )}
        {sections.map((section) => (
          <Fragment key={section.heading ?? ""}>
            {section.heading !== undefined && (
              <h2 class="heading">{section.heading}</h2>
            )}
            {section.albums.map((album) => (
              <AlbumCard
                key={album.id}
                name={album.name}
                artist={album.artist}
                image={album.image}
                shelf={badge(album, route)}
                playing={tileState(playing, album.id)}
                onOpen={() => {
                  go(`album/${album.id}`);
                }}
              />
            ))}
          </Fragment>
        ))}
      </div>
      {marks.length > 0 && (
        <HeadingJump
          class="jump"
          grid={grid}
          marks={marks}
          sized={strip === "sized"}
          label={
            strip === "sized" ? "Jump to a release date" : "Jump to a letter"
          }
        />
      )}
      <ToucanFab
        class="fab"
        bar
        label="Add an album"
        onClick={() => {
          go("search");
        }}
      />
    </Page>
  );
}
